import { includes, get, isMatch, toLower, merge, map } from 'lodash';
import classNames from 'classnames';
import React, { Component, PropTypes } from 'react';
import { bylineToString, getLocalLink } from 'utils/NewsUtils';
import { showWindow, getDataOfWindowOpen } from 'utils/WindowUtils';
import { filterItems } from 'utils/CategoryUtils';
import Timestamp from 'components/Timestamp';
import Image from 'components/Image';
import ComponentTitle from 'components/shared/ComponentTitle';
import { COLOR_STYLE } from '../../constants/FRNBaseStyles';
import {withResources} from 'lib/redux';

const CARD_TYPE = {
  split: 'split',
  full: 'full',
  text: 'text'
};

const WIDTH_STYLE = {
  sm: 'sm',
  md: 'sm',
  lg: 'lg'
};

const RATIO_SPLIT_CARD_IMAGE = {
  sm: 1, // full width
  md: 0.5, // 50% card width
  lg: 0.66 // 66% card width
};

class Card extends Component {
  static propTypes = {
    FRN_rawResponses: PropTypes.array,
    cardType: PropTypes.oneOf(Object.keys(CARD_TYPE)),
    widthStyle: PropTypes.oneOf(Object.keys(WIDTH_STYLE)),
    heightStyle: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
    thumbnailAlignment: PropTypes.oneOf(['left', 'right']),
    textAlignment: PropTypes.oneOf(['left', 'right']),
    gradientDirection: PropTypes.oneOf(['left', 'right', 'bottom', 'top']),
    textVerticalAlignment: PropTypes.oneOf(['top', 'bottom']),
    videoIconPlacement: PropTypes.oneOf(['image-center', 'image-bottom-left', 'image-bottom-right', 'image-top-left', 'image-top-right', 'inline-left', 'inline-right']),
    startArticleIndex: PropTypes.number,
    types: PropTypes.array,
    displaySizes: PropTypes.array,
    backgroundColorStyle: PropTypes.oneOf(Object.keys(COLOR_STYLE)),
    backgroundColor: PropTypes.string,
    textColorStyle: PropTypes.oneOf(Object.keys(COLOR_STYLE)),
    textColor: PropTypes.string,
    showDescriptions: PropTypes.bool,
    showPlayButtonForVideo: PropTypes.bool,
    showTimestamp: PropTypes.bool,
    showUpdatedDate: PropTypes.bool,
    showBylines: PropTypes.bool,
    edgeToEdge: PropTypes.bool,
    timestampOptions: PropTypes.shape({
      showElapsedTime: PropTypes.bool,
      displayShortDateTime: PropTypes.bool
    }),
    maintainImageAspectRatio: PropTypes.bool,
    imageAspectRatioBackgroundColor: PropTypes.string,
    pillOptions: PropTypes.shape({
      pillText: PropTypes.string,
      pillColorStyle: PropTypes.oneOf(Object.keys(COLOR_STYLE)),
      pillColor: PropTypes.string,
      pillVerticalAlign: PropTypes.oneOf(['top-card', 'above-headline'])
    }),
    displayTriggers: PropTypes.arrayOf(
      PropTypes.shape({
        filter: PropTypes.object.isRequired,
        props: PropTypes.shape({
          textColorStyle: PropTypes.oneOf(Object.keys(COLOR_STYLE)),
          textColor: PropTypes.string,
          backgroundColorStyle: PropTypes.oneOf(Object.keys(COLOR_STYLE)),
          backgroundColor: PropTypes.string,
          pillText: PropTypes.string,
          pillColorStyle: PropTypes.oneOf(Object.keys(COLOR_STYLE)),
          pillColor: PropTypes.string
        })
      })
    ),
    state: PropTypes.object,
    spans: PropTypes.object,
    title: PropTypes.string,
    titleColor: PropTypes.string,
    titleType: PropTypes.string
  }

  static defaultProps = {
    cardType: 'split',
    widthStyle: 'sm',
    heightStyle: 'md',
    thumbnailAlignment: 'right',
    textAlignment: 'left',
    gradientDirection: 'bottom',
    textVerticalAlignment: 'bottom',
    videoIconPlacement: 'inline-right',
    startArticleIndex: 0,
    types: ['story', 'clip', 'link'],
    showDescriptions: false,
    showPlayButtonForVideo: true,
    showTimestamp: true,
    showUpdatedDate: false,
    showBylines: true,
    edgeToEdge: false,
    timestampOptions: {
      showElapsedTime: true,
      displayShortDateTime: true
    },
    maintainImageAspectRatio: false,
    pillOptions: {
      pillVerticalAlign: 'top-card'
    },
    title: '',
    titleType: 'Module Title'
  }

  render() {
    const {
      FRN_rawResponses: [
        {
          data: {
            features: items = []
          } = {}
        } = {}
      ] = [],
      cardType,
      widthStyle,
      heightStyle,
      startArticleIndex,
      types: itemTypes,
      displaySizes = [],
      thumbnailAlignment,
      textAlignment,
      gradientDirection,
      textVerticalAlignment,
      videoIconPlacement,
      showDescriptions,
      backgroundColorStyle: defaultBackgroundColorStyle,
      backgroundColor: defaultBackgroundColor,
      textColorStyle: defaultTextColorStyle,
      textColor: defaultTextColor,
      showPlayButtonForVideo,
      showTimestamp,
      showUpdatedDate,
      showBylines,
      timestampOptions: {
        showElapsedTime,
        displayShortDateTime
      },
      maintainImageAspectRatio,
      imageAspectRatioBackgroundColor,
      pillOptions: {
        pillText: defaultPillText,
        pillColorStyle: defaultPillColorStyle,
        pillColor: defaultPillColor,
        pillVerticalAlign
      },
      displayTriggers = [],
      edgeToEdge,
      spans = {},
      title,
      titleType,
      titleColor
    } = this.props;

    const offset = Math.max(startArticleIndex, 0);
    const filteredItems = filterItems(items, {type: itemTypes, displaysize: displaySizes });
    const itemToShow = filteredItems[offset];

    if (!itemToShow) {
      return null;
    }

    const customOption = displayTriggers.find((displayTriggersItem) => isMatch(itemToShow, displayTriggersItem.filter)) || {};
    const {
      props: {
        textColorStyle: customTextColorStyle,
        textColor: customTextColor,
        backgroundColorStyle: customBackgroundColorStyle,
        backgroundColor: customBackgroundColor,
        pillText: customPillText,
        pillColorStyle: customPillColorStyle,
        pillColor: customPillColor
      } = {}
    } = customOption;

    const pillText = customPillText || defaultPillText;
    const backgroundColorStyle = customBackgroundColorStyle || defaultBackgroundColorStyle;
    const backgroundColor = customBackgroundColor || (!customBackgroundColorStyle ? defaultBackgroundColor : '');
    const textColorStyle = customTextColorStyle || defaultTextColorStyle;
    const textColor = customTextColor || (!customTextColorStyle ? defaultTextColor : '');
    const pillColorStyle = customPillColorStyle || defaultPillColorStyle;
    const pillColor = customPillColor || (!customPillColorStyle ? defaultPillColor : '');

    const {
      id = '',
      url = '',
      link = '',
      layout = '',
      headline = '',
      updatedDate = '',
      publishedDate = '',
      type: newsType = '',
      abstract: description = '',
      seo: { pageurl: slug = ''} = {},
      bylines = [],
      surfaceable: [ { type: featureType = '' } = {} ] = [],
      images = {},
      abstractimage: {
        filename: newsImage = '',
        lastEditedDate = ''
      } = {},
      window: {
        height = '500',
        width = '500',
        isnewwindow = false,
        showtoolbar = true,
        isresizable = true,
        showscrollbar = true,
        showlocation = true,
      } = {},
      options: {
        date: {
          published: {
            showonabstract: showPublished = true
          } = {},
          updated: {
            showonabstract: showUpdated = true
          } = {}
        } = {}
      } = {}
    } = itemToShow;

    let type = cardType;
    let locationUrl = '';

    try {
      if (typeof location === 'undefined') {
        const {hostname, port, protocol} = this.props.state.page;
        const location = {hostname, port, protocol: protocol.endsWith(':') ? protocol : protocol + ':'};
        locationUrl = getLocalLink(location, {id, type: newsType, slug, link});
      } else {
        locationUrl = getLocalLink(location, {id, type: newsType, slug, link});
      }
    } catch (err) {
      console.log('>>> getLocalLink error', err);
    }

    const isSplitCard = type === CARD_TYPE.split;
    const isFullCard = type === CARD_TYPE.full;
    const isSmStyle = widthStyle === WIDTH_STYLE.sm;
    const isTextAlignedTop = textVerticalAlignment === 'top';
    const isPillAlignedAboveHeadline = pillVerticalAlign === 'above-headline';
    const isPillAlignedTopCard = pillVerticalAlign === 'top-card';

    const isSmSplitCard = isSplitCard && isSmStyle;

    const textClassNames = classNames(
      'Card-content',
      `Card-${type}-content`,
      {[`Card-${type}-content--${textVerticalAlignment}`]: !isSmStyle || !isSplitCard},
      {[`Card-${type}-content--${textVerticalAlignment}-noTimestamp`]: !(showTimestamp || showBylines)},
      {[`Card-${type}-content--${textVerticalAlignment}-noPillPadding`]: !pillText || (isTextAlignedTop && isPillAlignedAboveHeadline) }
    );

    const showVideoIcon = showPlayButtonForVideo && (newsType.toLowerCase() === 'clip' || featureType.toLowerCase() === 'clip');
    const videoIconPosition = videoIconPlacement.slice(videoIconPlacement.indexOf('-') + 1, videoIconPlacement.length);
    // Keeping space between pill and title if they stick together
    const isTitleStickToPill = pillText && !isSmSplitCard && (isPillAlignedAboveHeadline || (isPillAlignedTopCard && isTextAlignedTop));

    const titleClassnames = classNames(
      'Card-title',
      {'Card-title-stickToPill': isTitleStickToPill},
      `Card-${type}-title`,
      `Card-${type}-title--${widthStyle}`,
      {[`TilteVideoThumbnail TilteVideoThumbnail-${videoIconPosition}`]: showVideoIcon && videoIconPlacement.includes('inline')}
    );

    const pillTextClassnames = classNames(
      'Card-pillText',
      {'Card-pillText--noBackground': !(pillColor || pillColorStyle)},
      {[`frn-u-background-${pillColorStyle}`]: pillColorStyle}
    );

    const pillContent = pillText ? <div className={`Card-pill Card-${type} Card-pill--${pillVerticalAlign} Card-${type}-pill--${pillVerticalAlign}`}><span className={pillTextClassnames} style={{backgroundColor: pillColor}}>{pillText}</span></div> : null;

    const textContainerClassnames = classNames(
      'Card-textContainer',
      `Card-${type}-textContainer`,
      `Card-${type}-textContainer--${widthStyle}`,
      `Card-${type}-textContainer--${textAlignment}`,
      {[`Card-${type}-textContainer--${widthStyle}-static-top`]: pillText && isPillAlignedTopCard}
    );

    const pillAboveHeadlineInSmSplit = isPillAlignedAboveHeadline && isSmSplitCard;

    const content = [];

    const iframeImage = get(images, 'graphic.filename', '');
    const linkImage = get(images, 'abstract.filename', '');

    const isShowWindowLayout = isnewwindow && (newsType === 'link');
    const urlIFrameOrUrlLoc = layout === 3 ? (url || iframeImage) : locationUrl;
    const dataOfWindowOpen = getDataOfWindowOpen(width, height, showtoolbar, showscrollbar, showlocation, isresizable);
    let timestamp;

    if (showTimestamp || showBylines) {
      let timestampDate;

      if (showTimestamp) {
        const publishedDateTimestamp = showPublished ? publishedDate : null;
        const updatedDateTimestamp = showUpdated ? updatedDate : null;

        timestampDate = showUpdatedDate ? updatedDateTimestamp : publishedDateTimestamp;
      }

      timestamp = <Timestamp publishDate={timestampDate} showElapsedTime={showElapsedTime} displayShortDateTime={displayShortDateTime} authors={showBylines ? map(bylines, bylineToString) : null} />;
    }

    const isOnClick = () => {isShowWindowLayout ? showWindow(urlIFrameOrUrlLoc, '_blank', dataOfWindowOpen) : showWindow(urlIFrameOrUrlLoc, '_self');};

    content.push(
      <div key={content.length} className={textContainerClassnames}>
        { (isPillAlignedTopCard || pillAboveHeadlineInSmSplit) && pillContent }
        <div className={textClassNames}>
          { (isPillAlignedAboveHeadline && !pillAboveHeadlineInSmSplit) && pillContent }
          { isShowWindowLayout ?
            <a>
              <div className={titleClassnames}>{headline}</div>
            </a> :
            <a href={ isnewwindow ? null : urlIFrameOrUrlLoc}><div className={titleClassnames}>{headline}</div></a>}
          {showDescriptions && <p className={`Card-description Card-${type}-description Card-description Card-${type}-description--${widthStyle}`} dangerouslySetInnerHTML={{ __html: description.replace(/p>/g, 'span>') }}></p>}
        </div>
        {timestamp}
      </div>
    );

    const customBackgroundStyles = {};

    customBackgroundStyles['backgroundColor'] = backgroundColor;

    if (maintainImageAspectRatio) {
      customBackgroundStyles['backgroundColor'] = imageAspectRatioBackgroundColor;

      if (isSmSplitCard || isFullCard) {
        const DEFAULT_TRANSPARENT = '60%';
        const BACKGROUND_TRANSPARENT = {
          sm: '90%',
          md: DEFAULT_TRANSPARENT,
          lg: '30%'
        };
        /* background-image is scaled to width proportionally */
        customBackgroundStyles['backgroundSize'] = '100% auto';
        customBackgroundStyles['backgroundPosition'] = 'top center, bottom';
        /* add linear-gradient for backgroundImage */
        const transparentValue = (gradientDirection === 'top' || gradientDirection === 'bottom') ? DEFAULT_TRANSPARENT : BACKGROUND_TRANSPARENT[widthStyle];
        customBackgroundStyles['backgroundImage'] += `,linear-gradient(to ${gradientDirection}, ${imageAspectRatioBackgroundColor}, transparent ${transparentValue})`;
      }
    } else {
      customBackgroundStyles['backgroundSize'] = 'cover';
    }

    /* Number of columns for each breakpoint, default: xs = 12, sm = xs, md = sm, lg = md, xl = lg */
    const {
      xs = 12,
      sm = xs,
      md = sm,
      lg = md,
      xl = lg
    } = spans;

    const ratioImageWidth = isSplitCard ? RATIO_SPLIT_CARD_IMAGE[widthStyle] : 1;

    /* Breakpoints: XS: 0-767, S: 768-991, M: 992-1199, L: 1200-1469, XL: 1470-  */
    const imageBreakpoints = [
      { "maxWidth": 640, "widthVW": 100},
      { "maxWidth": 767, "widthVW": Math.round((xs / 12) * 100) },
      { "maxWidth": 991, "widthPX": Math.round((sm / 12) * 720 * ratioImageWidth)},
      { "maxWidth": 1199, "widthPX": Math.round((md / 12) * 940 * ratioImageWidth)},
      { "maxWidth": 1469, "widthPX": Math.round((lg / 12) * 1140 * ratioImageWidth)},
      {                   "widthPX": Math.round((xl / 12) * 1410 * ratioImageWidth)},
    ];

    if (isSplitCard) {
      const imageContainerClassNames = classNames(
        `Card-split-imageContainer`,
        {[`Card-imageGradient Card-imageGradient--${gradientDirection}`]: isSmSplitCard},
        `Card-split-imageContainer--${widthStyle}`,
        {[`VideoThumbnail VideoThumbnail-${videoIconPosition}`]: showVideoIcon && videoIconPlacement.includes('image')}
      );

      const imageStyles = maintainImageAspectRatio ? {
        objectFit: 'contain'
      } : null;

      const thumbnailContent = (
        <a key={content.length}
          href={ isnewwindow ? null : urlIFrameOrUrlLoc}
          className={imageContainerClassNames}
          style={customBackgroundStyles}>
          <Image imageClassNames="img-responsive" lastEditedDate={lastEditedDate} src={ linkImage || newsImage } alt={headline} imageStyles={imageStyles} imageBreakpoints={imageBreakpoints} widthStyle={widthStyle}/>
        </a>
      );

      if (thumbnailAlignment === 'left' || isSmStyle) {
        content.unshift(thumbnailContent);
      } else {
        content.push(thumbnailContent);
      }
    } else { // for both fullCard & text cards, we should still use an image component so that we get the placeholderImage
      content.unshift(<Image key={content.length} lastEditedDate={lastEditedDate} imageClassNames="img-responsive" src={ linkImage || newsImage } alt={headline} imageBreakpoints={imageBreakpoints} widthStyle={widthStyle}/>);
    }

    const cardStyles = {
      color: textColor,
      backgroundColor
    };

    const cardClassNames = classNames(
      'Card-container',
      `Card-${heightStyle}`,
      `Card-${type}-container`,
      `Card-${type}-container--${widthStyle}`,
      {[`Card-imageGradient Card-imageGradient--${gradientDirection}`]: isFullCard},
      {[`frn-u-foreground-${textColorStyle}`]: textColorStyle},
      {[`frn-u-background-${backgroundColorStyle}`]: backgroundColorStyle}
    );

    const cardWrapperClassName = classNames('Card', 'Card-hoverTitle', {'Card-edgeToEdge': edgeToEdge});

    return (
      <div className={cardWrapperClassName} onClick={isOnClick}>
        { title ? <ComponentTitle titleType={titleType} color={titleColor} showArrow={false} title={title} /> : null }
        <div className={cardClassNames}
          style={isFullCard ? merge(cardStyles, customBackgroundStyles) : cardStyles}>
          {content}
        </div>
      </div>
    );
  }
}

export default withResources(Card);



// WEBPACK FOOTER //
// ./app/components/news/Card.js